/**
 * The design system's own strings, and the seam that lets an app translate them.
 *
 * `@erpx/ui` is presentational and dependency-light on purpose (see
 * `index.ts`), so it does not import vue-i18n: a component library that pulls in
 * an i18n runtime forces every consumer onto the same one. But a handful of
 * strings are unavoidably baked into the components — "Previous page", "Visible
 * columns", "No records match these filters" — and CLAUDE.md §6 says *all* UI
 * strings go through vue-i18n keys. Leaving these in English would put a wall of
 * untranslated chrome around otherwise translated screens.
 *
 * The resolution is an injection seam. The library ships a complete English
 * bundle as the default; an app calls `provideUiStrings()` once at its root with
 * a computed that reads its own vue-i18n catalogue, and every component picks
 * the translations up. Nothing here is required — an app that provides nothing
 * renders in English exactly as before.
 *
 *     // app root component
 *     const { t } = useI18n()
 *     provideUiStrings(computed(() => ({ dialogClose: t('common.actions.close'), … })))
 *
 * Values may carry `{name}` placeholders; {@link interpolate} fills them. Word
 * order differs between languages, so a sentence with a value in it is always
 * one template with a placeholder — never two fragments concatenated.
 */

import { computed, inject, provide, type ComputedRef, type InjectionKey } from 'vue'

export interface UiStrings {
  /* EButton */
  /** Announced while a button is busy. */
  buttonWorking: string

  /* EDialog / EToast */
  dialogClose: string
  toastDismiss: string

  /* EField */
  /** Visually hidden marker read after a required field's label. */
  fieldRequired: string
  /** Shown beside an optional field's label. */
  fieldOptional: string

  /* EMoney — accounting parentheses hide the sign, so it is spoken instead. */
  /** `{amount}` — the unsigned formatted figure. */
  moneyNegative: string

  /* ESelect */
  selectPlaceholder: string
  /** Label of the "no selection" option in a clearable select. */
  selectClear: string

  /* EPagination */
  paginationLabel: string
  paginationPrevious: string
  paginationPreviousPage: string
  paginationNext: string
  paginationNextPage: string
  paginationEmpty: string
  /** `{range}` — e.g. `1–50`. */
  paginationShowing: string
  /** `{range}`, `{total}`. */
  paginationShowingOfTotal: string
  /** `{size}` — rows per page. */
  paginationPerPage: string

  /* EDataTable */
  /** `{count}` — used when exactly one record matched. */
  tableRecordCountOne: string
  /** `{count}` — used for every other count, including zero. */
  tableRecordCountOther: string
  tableColumns: string
  tableChooseColumns: string
  tableVisibleColumns: string
  tableSelectAllRows: string
  /** `{index}` — 1-based row number. */
  tableSelectRow: string
  tableEmptyTitle: string
  tableFilteredTitle: string
  tableFilteredDescription: string
  tableClearFilters: string

  /* EPanel */
  panelEmptyTitle: string
  panelLoading: string

  /* ECommandPalette */
  paletteTitle: string
  paletteListLabel: string
  palettePlaceholder: string
  paletteEmpty: string
  paletteNavigateHint: string
  paletteRunHint: string
  paletteCloseHint: string
  paletteEscKey: string
}

/**
 * The library's English defaults — complete, and the fallback for any key an
 * app chooses not to override.
 */
export const UI_STRINGS_EN: UiStrings = {
  buttonWorking: 'Working',

  dialogClose: 'Close',
  toastDismiss: 'Dismiss',

  fieldRequired: '(required)',
  fieldOptional: 'optional',

  moneyNegative: 'negative {amount}',

  selectPlaceholder: 'Select…',
  selectClear: 'None',

  paginationLabel: 'Pagination',
  paginationPrevious: 'Previous',
  paginationPreviousPage: 'Previous page',
  paginationNext: 'Next',
  paginationNextPage: 'Next page',
  paginationEmpty: 'No records',
  paginationShowing: 'Showing {range}',
  paginationShowingOfTotal: 'Showing {range} of {total}',
  paginationPerPage: '{size} per page',

  tableRecordCountOne: '{count} record',
  tableRecordCountOther: '{count} records',
  tableColumns: 'Columns',
  tableChooseColumns: 'Choose columns',
  tableVisibleColumns: 'Visible columns',
  tableSelectAllRows: 'Select all rows on this page',
  tableSelectRow: 'Select row {index}',
  tableEmptyTitle: 'Nothing to show yet',
  tableFilteredTitle: 'No records match these filters',
  tableFilteredDescription: 'Try widening the date range or clearing a filter chip.',
  tableClearFilters: 'Clear filters',

  panelEmptyTitle: 'Nothing here yet',
  panelLoading: 'Loading',

  paletteTitle: 'Command palette',
  paletteListLabel: 'Commands',
  palettePlaceholder: 'Search commands, documents and reports…',
  paletteEmpty: 'No matching commands',
  paletteNavigateHint: 'to navigate',
  paletteRunHint: 'to run',
  paletteCloseHint: 'to close',
  paletteEscKey: 'Esc',
}

export type UiStringKey = keyof UiStrings

/** Every key the library expects an app catalogue to supply. */
export const UI_STRING_KEYS = Object.keys(UI_STRINGS_EN) as readonly UiStringKey[]

export const UI_STRINGS_KEY: InjectionKey<ComputedRef<Partial<UiStrings>>> = Symbol('erpx.ui.strings')

/**
 * Publish translated strings to every `@erpx/ui` component below this one.
 * Pass a computed so a locale switch re-renders without remounting the app.
 */
export function provideUiStrings(source: ComputedRef<Partial<UiStrings>>): void {
  provide(UI_STRINGS_KEY, source)
}

/**
 * The resolved bundle: whatever the app provided, with English underneath. Safe
 * to call from any component, provided or not.
 */
export function useUiStrings(): ComputedRef<UiStrings> {
  const provided = inject(UI_STRINGS_KEY, null)
  return computed(() => (provided === null ? UI_STRINGS_EN : { ...UI_STRINGS_EN, ...provided.value }))
}

/**
 * Fill `{name}` placeholders. An unknown placeholder is left as written rather
 * than replaced with `undefined`, so a catalogue typo is visible instead of
 * silently blanking a sentence.
 */
export function interpolate(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match,
  )
}
